/**
 * Single source of truth for the unified recommended jobs feed (Jobs tab + Agent).
 * Syncs recommended_jobs, hydrates peerlist_jobs, ranks by skills + Pinecone + account prefs.
 */
import { supabase } from '../lib/supabase';
import { roleAlignmentScore } from '../modules/recommendations/engine/scoring/dimensionScores';
import type { RecommendationResult } from '../modules/recommendations/types';
import type { Job } from '../modules/dashboard/components/JobCard';
import { cleanSkills, isJunkJob } from '../modules/dashboard/components/Jobs/helpers';
import {
  balanceChannelDisplayOrder,
  buildTitleIlikeOrFilter,
  extractRoleSearchTokens,
  extractRoleTargets,
  filterBlatantLocationConflicts,
  sortJobsByPreferenceRelevance,
} from '../modules/dashboard/components/Jobs/jobPreferenceFilter';
import { rankJobs } from '../modules/dashboard/components/Jobs/ranking';
import type { UserProfile } from '../types/userProfile';
import {
  filterJobsByProfileRelevance,
} from '../modules/ai-agent/utils/jobProfileMatch';
import {
  listRecommendedJobs,
  RECOMMENDED_POOL_LIMIT,
  syncRecommendedJobs,
} from './recommendedJobsService';

export { RECOMMENDED_POOL_LIMIT };

const JOB_FRESHNESS_DAYS = 180;
const PEERLIST_ID_CHUNK = 80;

const PEERLIST_JOBS_SELECT =
  'id, url, title, company, company_logo_url, location, remote, ' +
  'job_type, experience_level, salary_min, salary_max, salary_currency, ' +
  'skills, must_have_skills, good_to_have_skills, duration, ' +
  'compensation_text, description_md, posted_at, last_seen_at, external_slug, source_channel';

type PeerlistRow = {
  id: string;
  url: string;
  title: string;
  company: string;
  company_logo_url: string | null;
  location: string | null;
  remote: boolean | null;
  job_type: string | null;
  experience_level: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  skills: string[] | null;
  must_have_skills: string[] | null;
  good_to_have_skills: string[] | null;
  duration: string | null;
  compensation_text: string | null;
  description_md: string | null;
  posted_at: string | null;
  last_seen_at: string;
  external_slug: string;
  source_channel: string | null;
};

export type UnifiedFeedRankContext = {
  skills: Set<string>;
  titles: Set<string>;
  experienceYears?: number | null;
  headline?: string | null;
};

export type BuildUnifiedRecommendedFeedOptions = {
  limit?: number;
  dbRecs: RecommendationResult[];
  profile: UserProfile | null;
  profileJobTitle?: string;
  userCtx: UnifiedFeedRankContext | null;
  hasSkillContext: boolean;
  skillContextReady: boolean;
  /** When called from the agent, the user's search query. Passed to
   *  filterJobsByProfileRelevance so cross-domain explicit searches are preserved. */
  searchQuery?: string;
};

function mapAndFilterRows(rows: unknown[] | null): Job[] {
  const list = (rows ?? []) as PeerlistRow[];
  return list
    .map((r) => toJob(r))
    .filter(
      (j) =>
        !isJunkJob({
          company: j.company,
          title: j.title,
          skills: j.skills,
          description: j.description,
        }),
    );
}

function toJob(r: PeerlistRow): Job {
  const skills = cleanSkills(r.skills);
  const mustHave = cleanSkills(r.must_have_skills);
  const goodToHave = cleanSkills(r.good_to_have_skills);
  const channel = r.source_channel?.toLowerCase() ?? 'peerlist';
  return {
    id: r.id,
    source: channel === 'peerlist' ? 'peerlist' : 'direct',
    source_channel: channel !== 'peerlist' ? channel : undefined,
    title: r.title,
    company: r.company,
    company_logo_url: r.company_logo_url ?? undefined,
    location: r.location ?? '',
    url: r.url,
    created: r.last_seen_at,
    posted_at: r.posted_at ?? undefined,
    description: r.description_md ?? undefined,
    skills: skills.length > 0 ? skills : undefined,
    must_have_skills: mustHave.length > 0 ? mustHave : undefined,
    good_to_have_skills: goodToHave.length > 0 ? goodToHave : undefined,
    duration: r.duration ?? undefined,
    compensation_text: r.compensation_text ?? undefined,
    salary_min: r.salary_min ?? undefined,
    salary_max: r.salary_max ?? undefined,
    salary_currency: r.salary_currency ?? 'INR',
    job_type: r.job_type ?? undefined,
    experience_level: r.experience_level ?? undefined,
    remote: r.remote ?? undefined,
    external_job_id: r.external_slug,
  };
}

function peerlistFreshnessQuery(
  q: ReturnType<ReturnType<typeof supabase.from>['select']>,
  cutoffISO: string,
) {
  return q.or(`posted_at.gte.${cutoffISO},posted_at.is.null`);
}

async function fetchPeerlistJobsByIds(jobIds: string[]): Promise<Job[]> {
  if (jobIds.length === 0) return [];

  const byId = new Map<string, Job>();
  let totalRaw = 0;
  let totalJunk = 0;
  for (let i = 0; i < jobIds.length; i += PEERLIST_ID_CHUNK) {
    const chunk = jobIds.slice(i, i + PEERLIST_ID_CHUNK);
    const { data, error } = await supabase
      .from('peerlist_jobs')
      .select(PEERLIST_JOBS_SELECT)
      .in('id', chunk)
      .eq('is_active', true);

    if (error) {
      console.warn('[unifiedFeed] peerlist_jobs by id failed:', error.message);
      continue;
    }
    const rawCount = data?.length ?? 0;
    totalRaw += rawCount;
    const mapped = mapAndFilterRows(data);
    totalJunk += rawCount - mapped.length;
    for (const j of mapped) {
      byId.set(j.id, j);
    }
  }

  const result = jobIds
    .map((id) => byId.get(id))
    .filter((j): j is Job => j != null);

  if (result.length < jobIds.length) {
    console.warn(
      `[unifiedFeed] peerlist hydration: requested ${jobIds.length}, ` +
      `DB returned ${totalRaw} active, ${totalJunk} filtered as junk, ` +
      `${result.length} usable`,
    );
  }
  return result;
}

async function fetchLegacyInternalJobs(
  freshnessCutoff: string,
  recIds: string[],
  profileJobTitle: string | undefined,
  limit: number,
): Promise<Job[]> {
  const seen = new Set<string>();
  let rows: Job[] = [];

  if (recIds.length > 0) {
    const { data, error } = await peerlistFreshnessQuery(
      supabase
        .from('peerlist_jobs')
        .select(PEERLIST_JOBS_SELECT)
        .in('id', recIds)
        .eq('is_active', true),
      freshnessCutoff,
    );
    if (error) {
      console.warn('[unifiedFeed] legacy rec-id query failed:', error.message);
    }
    rows = mapAndFilterRows(data);
    rows.forEach((j) => seen.add(j.id));
    console.info(`[unifiedFeed] legacy step 1 (rec IDs ${recIds.length}): ${rows.length} jobs`);
  }

  if (rows.length < limit) {
    const { data: catalogData, error: catErr } = await peerlistFreshnessQuery(
      supabase
        .from('peerlist_jobs')
        .select(PEERLIST_JOBS_SELECT)
        .eq('is_active', true),
      freshnessCutoff,
    )
      .order('last_seen_at', { ascending: false })
      .limit(limit * 2);

    if (catErr) {
      console.warn('[unifiedFeed] legacy catalog query failed:', catErr.message);
    }
    const catalogMapped = mapAndFilterRows(catalogData);
    let added = 0;
    for (const j of catalogMapped) {
      if (!seen.has(j.id)) {
        seen.add(j.id);
        rows.push(j);
        added++;
      }
      if (rows.length >= limit) break;
    }
    console.info(`[unifiedFeed] legacy step 2 (catalog): DB returned ${catalogData?.length ?? 0}, mapped ${catalogMapped.length}, added ${added}`);
  }

  if (rows.length < limit && profileJobTitle?.trim()) {
    const tokens = profileJobTitle
      .toLowerCase()
      .split(/\s+/)
      .filter((t) => t.length > 2)
      .slice(0, 2);
    if (tokens.length > 0) {
      const orFilter = buildTitleIlikeOrFilter(tokens);
      if (!orFilter) {
        console.info('[unifiedFeed] legacy step 3: skipped (no safe title tokens)');
      } else {
      const { data: roleData, error: roleErr } = await peerlistFreshnessQuery(
        supabase
          .from('peerlist_jobs')
          .select(PEERLIST_JOBS_SELECT)
          .eq('is_active', true)
          .or(orFilter),
        freshnessCutoff,
      )
        .order('posted_at', { ascending: false })
        .limit(100);

      if (roleErr) {
        console.warn('[unifiedFeed] legacy role query failed:', roleErr.message);
      }
      let added = 0;
      for (const j of mapAndFilterRows(roleData)) {
        if (!seen.has(j.id)) {
          seen.add(j.id);
          rows.push(j);
          added++;
        }
        if (rows.length >= limit) break;
      }
      if (added > 0) {
        console.info(`[unifiedFeed] legacy step 3 (role="${profileJobTitle}"): added ${added}`);
      }
      }
    }
  }

  console.info(`[unifiedFeed] legacy total: ${rows.length} jobs (cutoff: ${freshnessCutoff})`);
  return rows.slice(0, limit);
}

async function supplementRoleMatchedJobsByChannel(
  rows: Job[],
  profile: UserProfile | null,
  profileJobTitle: string | undefined,
  perChannel = 80,
): Promise<Job[]> {
  const tokens = extractRoleSearchTokens(profile, profileJobTitle);
  const orFilter = buildTitleIlikeOrFilter(tokens);
  if (!orFilter) return rows;

  const seen = new Set(rows.map((j) => j.id));
  const merged = [...rows];

  const channelQueries = [
    supabase
      .from('peerlist_jobs')
      .select(PEERLIST_JOBS_SELECT)
      .eq('is_active', true)
      .or(`and(or(source_channel.is.null,source_channel.eq.peerlist),or(${orFilter}))`)
      .order('last_seen_at', { ascending: false })
      .limit(perChannel),
    supabase
      .from('peerlist_jobs')
      .select(PEERLIST_JOBS_SELECT)
      .eq('is_active', true)
      .eq('source_channel', 'linkedin')
      .or(orFilter)
      .order('last_seen_at', { ascending: false })
      .limit(perChannel),
    supabase
      .from('peerlist_jobs')
      .select(PEERLIST_JOBS_SELECT)
      .eq('is_active', true)
      .eq('source_channel', 'naukri')
      .or(orFilter)
      .order('last_seen_at', { ascending: false })
      .limit(perChannel),
  ];

  const results = await Promise.allSettled(channelQueries);
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value.error && import.meta.env.DEV) {
      console.warn('[unifiedFeed] channel supplement query failed:', r.value.error.message);
    }
    if (r.status !== 'fulfilled' || r.value.error) continue;
    for (const j of mapAndFilterRows(r.value.data)) {
      if (!seen.has(j.id)) {
        seen.add(j.id);
        merged.push(j);
      }
    }
  }

  if (import.meta.env.DEV) {
    console.info('[unifiedFeed] role supplement mix:', countJobsByChannel(merged));
  }

  return merged;
}

function overlayPineconeScores(
  rows: Job[],
  dbRecs: RecommendationResult[],
  profileJobTitle: string | undefined,
): Job[] {
  const pineconeScoreMap = new Map(dbRecs.map((r) => [r.job_id, r.score]));

  return rows.map((j) => {
    const pineconeScore = pineconeScoreMap.get(j.id);
    if (pineconeScore == null) return j;

    const domainScore = roleAlignmentScore(profileJobTitle, undefined, j.title);
    const isCrossDomain = domainScore !== 50 && domainScore < 30;
    if (isCrossDomain) {
      return { ...j, matchScore: Math.min(20, Math.round(pineconeScore * 0.35)) };
    }
    return { ...j, matchScore: Math.round(pineconeScore) };
  });
}

function finalizeRecJobPool(
  rows: Job[],
  opts: {
    dbRecs: RecommendationResult[];
    profileJobTitle: string | undefined;
    hasSkillContext: boolean;
    userCtx: UnifiedFeedRankContext | null;
    skillContextReady: boolean;
  },
): Job[] {
  const { dbRecs, profileJobTitle, hasSkillContext, userCtx, skillContextReady } = opts;

  let pool = rows;

  if (skillContextReady && hasSkillContext && userCtx) {
    pool = rankJobs(pool, userCtx);
  }

  if (dbRecs.length > 0) {
    pool = overlayPineconeScores(pool, dbRecs, profileJobTitle);
  }

  return balanceChannelDisplayOrder(pool);
}

export function countJobsByChannel(jobs: Job[]): {
  peerlist: number;
  linkedin: number;
  naukri: number;
} {
  return {
    peerlist: jobs.filter((j) => !j.source_channel || j.source === 'peerlist').length,
    linkedin: jobs.filter((j) => j.source_channel === 'linkedin').length,
    naukri: jobs.filter((j) => j.source_channel === 'naukri').length,
  };
}

/** Core pool: sync + hydrate + skill/Pinecone rank (no account-preference sort). */
export async function buildUnifiedRecommendedFeedCore(
  opts: BuildUnifiedRecommendedFeedOptions,
): Promise<Job[]> {
  const limit = opts.limit ?? RECOMMENDED_POOL_LIMIT;
  const freshnessCutoff = new Date(
    Date.now() - JOB_FRESHNESS_DAYS * 24 * 60 * 60 * 1000,
  ).toISOString();

  const syncResult = await syncRecommendedJobs(limit);
  if (syncResult?.total_for_user != null) {
    console.info('[unifiedFeed] recommended_jobs pool:', syncResult.total_for_user, 'rows');
  } else {
    console.warn('[unifiedFeed] syncRecommendedJobs returned null — RPC may have failed');
  }

  const recRows = await listRecommendedJobs(limit);
  const jobIds = recRows.map((r) => r.job_id).filter(Boolean);
  console.info(`[unifiedFeed] recommended job IDs: ${jobIds.length} (from ${recRows.length} rows)`);

  let rows: Job[];
  let hydrationPath: string;
  if (jobIds.length === 0) {
    hydrationPath = 'legacy-fallback (no recommended IDs)';
    rows = await fetchLegacyInternalJobs(
      freshnessCutoff,
      opts.dbRecs.map((r) => r.job_id),
      opts.profileJobTitle,
      limit,
    );
  } else {
    rows = await fetchPeerlistJobsByIds(jobIds);
    if (rows.length === 0) {
      hydrationPath = 'legacy-fallback (hydration returned 0)';
      rows = await fetchLegacyInternalJobs(
        freshnessCutoff,
        opts.dbRecs.map((r) => r.job_id),
        opts.profileJobTitle,
        limit,
      );
    } else {
      hydrationPath = `peerlist_jobs hydration (${rows.length}/${jobIds.length} active)`;
    }
  }
  console.info(`[unifiedFeed] ${hydrationPath} → ${rows.length} jobs`);

  const beforeSupplement = rows.length;
  rows = await supplementRoleMatchedJobsByChannel(
    rows,
    opts.profile,
    opts.profileJobTitle,
    Math.max(60, Math.floor(limit / 3)),
  );
  if (rows.length > beforeSupplement) {
    console.info(`[unifiedFeed] role supplement: ${beforeSupplement} → ${rows.length} jobs`);
  }

  const pool = finalizeRecJobPool(rows, {
    dbRecs: opts.dbRecs,
    profileJobTitle: opts.profileJobTitle,
    hasSkillContext: opts.hasSkillContext,
    userCtx: opts.userCtx,
    skillContextReady: opts.skillContextReady,
  });

  console.info('[unifiedFeed] feed mix:', { total: pool.length, ...countJobsByChannel(pool) });

  return pool;
}

/** Same ordered pool as Jobs tab `recommendedBase` (default filters, no toolbar). */
export async function buildUnifiedRecommendedFeed(
  opts: BuildUnifiedRecommendedFeedOptions,
): Promise<Job[]> {
  let pool = await buildUnifiedRecommendedFeedCore(opts);

  if (import.meta.env.DEV) {
    console.info('[unifiedFeed] pre-filter pool:', pool.length, 'jobs');
  }

  if (opts.profile) {
    const roleTargets = extractRoleTargets(opts.profile, opts.profileJobTitle);
    pool = filterBlatantLocationConflicts(pool, opts.profile);
    pool = sortJobsByPreferenceRelevance(pool, opts.profile, roleTargets);

    const beforeFilter = pool.length;
    pool = filterJobsByProfileRelevance(pool, {
      currentRole: opts.profile.currentRole,
      headline: opts.profile.headline,
      skills: opts.profile.skills,
      experienceYears: opts.profile.experienceYears,
      location: opts.profile.location,
    }, opts.searchQuery);

    // Guard: if profile relevance filter dropped too aggressively, relax it
    if (pool.length < 5 && beforeFilter >= 5) {
      console.warn(
        `[unifiedFeed] Profile relevance filter too aggressive: ${beforeFilter} → ${pool.length}. ` +
        'Falling back to location-filtered pool.',
      );
      pool = filterBlatantLocationConflicts(
        await buildUnifiedRecommendedFeedCore(opts),
        opts.profile,
      );
      pool = sortJobsByPreferenceRelevance(pool, opts.profile, roleTargets);
    }
  }

  return balanceChannelDisplayOrder(pool);
}

/** Map Jobs-tab Job rows to AgentJobsPanel shape (preserves order). */
export function jobsToAgentJobs(jobs: Job[]): import('../modules/ai-agent/components/AgentJobsPanel').AgentJob[] {
  return jobs.map((j) => ({
    id: j.id,
    title: j.title,
    company: j.company,
    company_logo_url: j.company_logo_url ?? null,
    location: j.location ?? null,
    remote: j.remote ?? null,
    job_type: j.job_type ?? null,
    experience_level: j.experience_level ?? null,
    skills: j.skills ?? null,
    must_have_skills: j.must_have_skills ?? null,
    good_to_have_skills: j.good_to_have_skills ?? null,
    url: j.url ?? null,
    salary_min: j.salary_min ?? null,
    salary_max: j.salary_max ?? null,
    salary_currency: j.salary_currency ?? null,
    compensation_text: j.compensation_text ?? null,
    posted_at: j.posted_at ?? null,
    description_md: j.description ?? null,
    source_channel: j.source_channel ?? (j.source === 'peerlist' ? 'peerlist' : j.source),
  }));
}
